import { Head, router } from '@inertiajs/react'
import { useState, useRef, useCallback, useEffect } from 'react'
import { loadStripe, Stripe, StripeCardNumberElement, StripeElements } from '@stripe/stripe-js'
import ReservationLayout from '@/components/layouts/ReservationLayout'
import PrescriptionNotice from '@/components/reservations/PrescriptionNotice'
import { useRemainingTime } from '@/hooks/useRemainingTime'
import type { ProvisionalReservation, PatientFamilyProfile } from '@/types/schedule'

type Props = {
  provisional_reservation: ProvisionalReservation
  patient_family_profile: PatientFamilyProfile
  has_payment_method: boolean
  card_last4: string | null
  card_brand: string | null
  stripe_publishable_key: string
  payment_method_saved: boolean
  errors: Record<string, string[]>
}

const CARD_BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  jcb: 'JCB',
  diners: 'Diners Club',
  discover: 'Discover',
}

export default function InterviewStep3({
  provisional_reservation,
  patient_family_profile,
  has_payment_method,
  card_last4,
  card_brand,
  stripe_publishable_key,
  payment_method_saved: _payment_method_saved,
  errors,
}: Props) {
  const { deadlineTime, isExpired } = useRemainingTime(provisional_reservation.expires_at)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasPaymentMethod, setHasPaymentMethod] = useState(has_payment_method)
  const [currentCardLast4, setCurrentCardLast4] = useState(card_last4)
  const [currentCardBrand, setCurrentCardBrand] = useState(card_brand)

  // カード登録モーダル関連
  const [showCardModal, setShowCardModal] = useState(false)
  const [cardProcessing, setCardProcessing] = useState(false)
  const [cardError, setCardError] = useState<string | null>(null)

  // Stripe refs
  const stripeRef = useRef<Stripe | null>(null)
  const elementsRef = useRef<StripeElements | null>(null)
  const cardNumberRef = useRef<StripeCardNumberElement | null>(null)

  // Stripe Elementsのクリーンアップ
  const cleanupStripeElements = useCallback(() => {
    if (cardNumberRef.current) {
      cardNumberRef.current.unmount()
      cardNumberRef.current = null
    }
    elementsRef.current = null
  }, [])

  // Stripe Elements初期化
  const initializeStripeElements = useCallback(async () => {
    // 既存のElementsがあればクリーンアップ
    cleanupStripeElements()

    // Stripeインスタンスの初期化（1回だけ）
    if (!stripeRef.current) {
      const stripe = await loadStripe(stripe_publishable_key)
      if (!stripe) {
        setCardError('Stripeの初期化に失敗しました')
        return
      }
      stripeRef.current = stripe
    }

    const stripe = stripeRef.current
    const elements = stripe.elements()
    elementsRef.current = elements

    const style = {
      base: {
        fontSize: '16px',
        color: '#374151',
        '::placeholder': { color: '#9ca3af' },
      },
      invalid: {
        color: '#dc2626',
      },
    }

    // カード番号
    const cardNumber = elements.create('cardNumber', { style })
    cardNumber.mount('#card-number')
    cardNumberRef.current = cardNumber

    // 有効期限
    const cardExpiry = elements.create('cardExpiry', { style })
    cardExpiry.mount('#card-expiry')

    // CVC
    const cardCvc = elements.create('cardCvc', { style })
    cardCvc.mount('#card-cvc')
  }, [stripe_publishable_key, cleanupStripeElements])

  // モーダルが開いたらStripe Elementsを初期化、閉じたらクリーンアップ
  useEffect(() => {
    if (showCardModal) {
      // DOM要素が準備されるのを待ってから初期化
      const timer = setTimeout(() => {
        initializeStripeElements()
      }, 100)
      return () => {
        clearTimeout(timer)
        cleanupStripeElements()
      }
    }
  }, [showCardModal, initializeStripeElements, cleanupStripeElements])

  // クレジットカード登録処理
  const handleCardRegistration = useCallback(async () => {
    if (!stripeRef.current || !cardNumberRef.current) {
      setCardError('Stripeが初期化されていません')
      return
    }

    setCardProcessing(true)
    setCardError(null)

    try {
      // SetupIntentを作成
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      const setupResponse = await fetch(
        `/online_clinic/reservations/setup_intent?provisional_reservation_token=${provisional_reservation.token}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
        }
      )
      const setupData = await setupResponse.json()

      // カード情報を確認
      const { error, setupIntent } = await stripeRef.current.confirmCardSetup(setupData.client_secret, {
        payment_method: {
          card: cardNumberRef.current,
        },
      })

      if (error) {
        setCardError(error.message || 'カード登録に失敗しました')
        setCardProcessing(false)
        return
      }

      if (setupIntent?.status === 'succeeded') {
        // サーバーにカード登録を通知し、カード情報を取得
        const connectResponse = await fetch(
          `/online_clinic/reservations/connect_stripe_customer?provisional_reservation_token=${provisional_reservation.token}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken,
            },
            body: JSON.stringify({ setup_intent_id: setupData.setup_intent_id }),
          }
        )
        const connectData = await connectResponse.json()

        // カード情報を設定
        if (connectData.card_last4) {
          setCurrentCardLast4(connectData.card_last4)
        }
        if (connectData.card_brand) {
          setCurrentCardBrand(connectData.card_brand)
        }

        setHasPaymentMethod(true)
        setShowCardModal(false)
      }
    } catch (err) {
      setCardError('カード登録処理中にエラーが発生しました')
    } finally {
      setCardProcessing(false)
    }
  }, [provisional_reservation.token])

  // フォーム送信
  const handleSubmit = useCallback(() => {
    if (!hasPaymentMethod) {
      setShowCardModal(true)
      return
    }

    setIsSubmitting(true)

    router.post(
      `/online_clinic/reservations/update_interview_step3?provisional_reservation_token=${provisional_reservation.token}`,
      {},
      {
        onFinish: () => {
          setIsSubmitting(false)
        },
      }
    )
  }, [hasPaymentMethod, provisional_reservation.token])

  const previousPageLink = `/online_clinic/reservations/interview_step2?provisional_reservation_token=${provisional_reservation.token}`

  const formatCardBrand = (brand: string | null): string => {
    if (!brand) return ''
    return CARD_BRAND_LABELS[brand.toLowerCase()] || brand
  }

  return (
    <>
      <Head title="お支払い情報 - オンライン診療予約" />

      <ReservationLayout
        currentStep="interview3"
        previousPageLink={previousPageLink}
        title="お支払い情報"
        expiresAt={provisional_reservation.expires_at}
      >
        <div className="space-y-6">
          {/* 仮予約情報 */}
          <div className="oc-info-card">
            <div className="flex justify-between items-start mb-3">
              <h3 className="text-base font-semibold text-[#2a3039]">仮予約中の診療枠</h3>
              <div className="text-sm">
                <span className="text-[#6f7680]">確保期限: </span>
                <span className={`font-medium ${isExpired ? 'text-red-600' : 'text-cyan-600'}`}>
                  {isExpired ? '期限切れ' : `${deadlineTime}まで`}
                </span>
              </div>
            </div>
            <div className="space-y-1 text-sm text-[#515862]">
              <p>
                <span className="text-[#6f7680]">患者: </span>
                {patient_family_profile.full_name}
              </p>
              <p>
                <span className="text-[#6f7680]">診療科目: </span>
                {provisional_reservation.medical_department.name}
              </p>
              <p>
                <span className="text-[#6f7680]">日時: </span>
                {provisional_reservation.doctor_schedule.formatted_date}{' '}
                {provisional_reservation.doctor_schedule.formatted_time}
              </p>
              <p>
                <span className="text-[#6f7680]">担当医師: </span>
                {provisional_reservation.doctor_schedule.doctor.full_name}
              </p>
              <PrescriptionNotice
                nightSlot={provisional_reservation.doctor_schedule.night_slot}
                expectedPrescriptionIssuedDate={provisional_reservation.doctor_schedule.expected_prescription_issued_date}
                pharmacyReceivableFrom={provisional_reservation.doctor_schedule.pharmacy_receivable_from}
                className="mt-1"
              />
            </div>
          </div>

          {/* クレジットカード情報 */}
          <div className="oc-card">
            <h2 className="oc-section-title">{hasPaymentMethod ? '既存のカードを利用する' : 'お支払い方法の登録'}</h2>
            <p className="mb-6 text-sm text-[#6f7680]">{hasPaymentMethod ? '使用するカードを選択してください。' : '診療費のお支払いに使用するクレジットカードを登録してください。'}</p>

            {/* エラー表示 */}
            {Object.keys(errors).length > 0 && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
                <ul className="text-sm text-red-600 list-disc list-inside">
                  {Object.entries(errors).map(([key, messages]) =>
                    messages.map((msg, i) => <li key={`${key}-${i}`}>{msg}</li>)
                  )}
                </ul>
              </div>
            )}

            {hasPaymentMethod ? (
              // 登録済みカード表示
              <div className="rounded-xl border border-[#d8dde3] bg-white p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-12 items-center justify-center rounded bg-[#eef3f7]">
                      <svg className="w-8 h-5 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">
                        下4桁 {currentCardLast4}
                      </p>
                      <p className="text-sm text-gray-500">{formatCardBrand(currentCardBrand)}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCardModal(true)}
                    className="text-sm font-medium text-[#2196f3] hover:underline"
                  >
                    変更する
                  </button>
                </div>
              </div>
            ) : (
              // カード未登録時
              <div className="rounded-lg border border-[#d8dde3] bg-[#eef3f7] p-6 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                  </svg>
                </div>
                <p className="text-gray-600 mb-4">クレジットカードが登録されていません</p>
                <button
                  type="button"
                  onClick={() => setShowCardModal(true)}
                  className="button-secondary !inline-flex !w-auto px-6"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  クレジットカードを登録
                </button>
              </div>
            )}

            {/* 対応カードブランド */}
            <div className="mt-6">
              <p className="text-sm text-gray-500 mb-2">対応カードブランド</p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600">Visa</span>
                <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600">Mastercard</span>
                <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600">American Express</span>
                <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600">JCB</span>
              </div>
            </div>
          </div>

          {/* 料金案内 */}
          <div className="oc-card">
            <h2 className="oc-section-title">料金について</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">オンライン診療手数料</span>
                <span className="font-medium">1,100円（税込）</span>
              </div>

              <div className="flex justify-between py-2">
                <span className="text-gray-600">保険診療費</span>
                <span className="text-gray-500">診察後に確定</span>
              </div>
            </div>
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
              予約時に2,000円の仮決済を行います。実際の請求は診察完了後に行われます。
            </div>
          </div>
        </div>

        {/* ナビゲーションボタン */}
        <div className="oc-action-bar">
          <div className="oc-action-container">
            <button
              type="button"
              onClick={() => router.visit(previousPageLink)}
              className="button-secondary"
            >
              前へ
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="button-primary"
            >
              {isSubmitting ? '処理中...' : hasPaymentMethod ? '次へ' : 'カードを登録して次へ'}
            </button>
          </div>
        </div>

        {/* ボタン分のスペース確保 */}
        <div className="h-24" />

        {/* クレジットカード登録モーダル */}
        {showCardModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  {hasPaymentMethod ? 'クレジットカードを変更' : 'クレジットカード登録'}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowCardModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {hasPaymentMethod && currentCardLast4 && (
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs text-gray-500 mb-1">現在登録されているカード</p>
                  <div className="flex items-center gap-3">
                    <div className="flex h-6 w-9 items-center justify-center rounded bg-white">
                      <svg className="w-6 h-4 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        下4桁 {currentCardLast4}
                      </p>
                      <p className="text-xs text-gray-500">{formatCardBrand(currentCardBrand)}</p>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-sm text-gray-600 mb-4">
                {hasPaymentMethod
                  ? '新しいカード情報を入力すると、上記の登録済みカードと置き換わります。カード情報はStripeにより安全に処理されます。'
                  : '予約確定のためにクレジットカードを登録してください。カード情報はStripeにより安全に処理されます。'}
              </p>

              {cardError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">{cardError}</div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">カード番号</label>
                  <div id="card-number" className="p-3 border border-gray-300 rounded-lg bg-white"></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">有効期限</label>
                    <div id="card-expiry" className="p-3 border border-gray-300 rounded-lg bg-white"></div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">セキュリティコード</label>
                    <div id="card-cvc" className="p-3 border border-gray-300 rounded-lg bg-white"></div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCardModal(false)}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  onClick={handleCardRegistration}
                  disabled={cardProcessing}
                  className={`flex-1 py-2 px-4 rounded-lg text-white ${
                    cardProcessing ? 'bg-gray-300 cursor-not-allowed' : 'bg-cyan-500 hover:bg-cyan-600'
                  }`}
                >
                  {cardProcessing ? '処理中...' : hasPaymentMethod ? '変更する' : '登録する'}
                </button>
              </div>
            </div>
          </div>
        )}
      </ReservationLayout>
    </>
  )
}
